import { type ComputedRef, type InjectionKey } from 'vue'

import { useUserStore } from '@/stores/user.stores'

export type ResolvedUserLocaleContext = {
  intlLocale: ComputedRef<Intl.LocalesArgument>
  dayjsLocale: ComputedRef<string>
}

export const localeProviderKey = Symbol() as InjectionKey<ResolvedUserLocaleContext>

const isValidLocale = (value: string): boolean => {
  try {
    Intl.getCanonicalLocales(value)
    return true
  } catch {
    return false
  }
}

const normalizeLanguageCode = (value?: string) => {
  if (!value) {
    return undefined
  }

  return value.replace(/_/g, '-').trim()
}

const normalizeRegionCode = (value?: string) => {
  if (!value) {
    return undefined
  }

  return value.replace(/_/g, '-').trim().split('-').pop()?.toUpperCase()
}

export const resolveUserIntlLocale = (
  localeOverride?: Intl.LocalesArgument,
): Intl.LocalesArgument => {
  if (localeOverride) {
    const overrides = (Array.isArray(localeOverride) ? localeOverride : [localeOverride]).map(
      (locale) => locale.toString(),
    )
    const validOverrides = overrides.filter(isValidLocale)
    if (validOverrides.length) {
      return validOverrides.length === 1 ? validOverrides[0] : validOverrides
    }
  }

  const userStore = useUserStore()
  const language = normalizeLanguageCode(userStore.user?.lang)
  const region = normalizeRegionCode(userStore.user?.locale)

  const candidates = [
    language && region ? `${language.split('-')[0]}-${region}` : undefined,
    language,
    typeof navigator !== 'undefined' ? navigator.language : undefined,
    'en-GB',
  ]
    .filter((candidate): candidate is string => Boolean(candidate))
    .filter(isValidLocale)

  const [supported] = Intl.NumberFormat.supportedLocalesOf(candidates)
  return supported || 'en-GB'
}
